<template>
  <div
      class="relative w-full overflow-hidden group h-full"
      ref="carousel"
      @mouseenter="stopAutoSlide"
      @mouseleave="startAutoSlide"
  >
    <div
        class="absolute inset-0 flex transition-transform duration-1000 ease-in-out"
        :style="{ transform: `translateX(-${currentIndex * 100}%)` }"
    >
      <template v-for="(media, index) in slides" :key="index">
        <!-- Image Slide -->
        <img
            v-if="media.image && media.image.url !== ''"
            :src="resolveMediaUrl(media.image.url)" v-bind="imgAttrs(resolveMediaUrl(media.image.url), '100vw')"
            class="w-full h-full  flex-shrink-0" :class="cover ? 'object-cover' : 'object-contain'"
            :alt="imgAlt(resolveMediaUrl(media.image.url), 'Slide ' + (index + 1))"
        />

        <!-- Video Slide -->
        <video
            v-else-if="media.video && media.video.link !== ''"
            class="w-full h-full object-contain flex-shrink-0"
            :src="resolveMediaUrl(media.video.link)"
            @play="pauseAutoSlide"
            @ended="resumeAutoSlide"
            playsinline
            autoplay
            muted
            :ref="(el) => { if (el) videoRefs[index] = el }"
        ></video>
        <video class="w-full h-full object-contain flex-shrink-0"
               v-else-if="media.video && media.video.url !== ''"
               :src="resolveVideo(media.video)"
               :poster="media.video.poster"
               controls
               muted
               playsinline
               autoplay
        />
      </template>
    </div>

    <div v-if="cast" class="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent"></div>

    <!-- Arrows (Only on Desktop & on Hover) -->
    <button
        v-if="slides.length > 1"
        @click="prevSlide"
        class="hidden md:flex absolute left-2 top-1/2 -translate-y-1/2 bg-black/50 p-2 rounded-full text-white hover:bg-black/80 transition opacity-0 group-hover:opacity-100"
    >
      ◀
    </button>
    <button
        v-if="slides.length > 1"
        @click="nextSlide"
        class="hidden md:flex absolute right-2 top-1/2 -translate-y-1/2 bg-black/50 p-2 rounded-full text-white hover:bg-black/80 transition opacity-0 group-hover:opacity-100"
    >
      ▶
    </button>

    <!-- Dots for Navigation -->
    <div v-if="slides.length > 1" class="absolute bottom-4 left-1/2 transform -translate-x-1/2 flex space-x-2">
      <span
          v-for="(media, index) in slides"
          :key="index"
          @click="changeSlide(index)"
          class="w-3 h-3 rounded-full cursor-pointer transition"
          :class="currentIndex === index ? 'bg-white scale-110' : 'bg-gray-400 opacity-60'"
      ></span>
    </div>
  </div>
</template>

<script setup>
import {useImageAlt} from "@/composables/useImageAlt.js";
const imgAlt = useImageAlt();
import {imgAttrs} from "@/utils/photoSizes.js";
import { ref, onMounted, onUnmounted, nextTick, watch } from "vue";
import {resolveMediaUrl, resolveVideo} from "@/composables/images.js";

let Hammer = null; // Prevent SSR from breaking

if (!import.meta.env.SSR) {
  import("hammerjs").then((module) => {
    Hammer = module.default;
  });
}

const props = defineProps({
  slides: { type: Array, required: true }, // Supports both images and videos
  cast: { type: Boolean, default: false },
  title: { type: String, default: "" },
  cover: {
    type: Boolean,
    default: true,
  }
});

const currentIndex = ref(0);
const carousel = ref(null);
const videoRefs = ref([]);
let autoSlideInterval = null;

// Determine if media is an image or video
const isVideo = (media) => /\.(mp4|webm|ogg)$/i.test(media);

// Auto-slide every 5 seconds (unless video is playing)
const startAutoSlide = () => {
  stopAutoSlide();
  autoSlideInterval = setInterval(() => {
    nextSlide();
  }, 5000);
};

const stopAutoSlide = () => {
  clearInterval(autoSlideInterval);
};

// Pause auto-slide when video plays
const pauseAutoSlide = () => {
  stopAutoSlide();
};

// Resume auto-slide when video ends
const resumeAutoSlide = () => {
  nextSlide();
  startAutoSlide();
};

// Reset video when revisited
const resetVideo = () => {
  nextTick(() => {
    const currentMedia = props.slides[currentIndex.value];
    if (isVideo(currentMedia)) {
      const videoEl = videoRefs.value[currentIndex.value];
      if (videoEl) {
        videoEl.pause();
        videoEl.currentTime = 0;
        videoEl.play();
      }
    }
  });
};

// Change slide and restart video if needed
const changeSlide = (index) => {
  currentIndex.value = index;
  resetVideo();
};

// Handle Next/Prev Slide
const nextSlide = () => {
  currentIndex.value = (currentIndex.value + 1) % props.slides.length;
  resetVideo();
};

const prevSlide = () => {
  currentIndex.value = (currentIndex.value - 1 + props.slides.length) % props.slides.length;
  resetVideo();
};

// Watch for slide changes to restart video
watch(currentIndex, () => {
  resetVideo();
});

// Enable Swipe Gestures
onMounted(() => {
  startAutoSlide();

  nextTick(() => {
    if (carousel.value && Hammer) {
      const hammer = new Hammer(carousel.value);
      hammer.on("swipeleft", nextSlide);
      hammer.on("swiperight", prevSlide);
    }
  });
});

onUnmounted(() => {
  stopAutoSlide();
});
</script>
